import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { StepByStepFlowStepId } from '@rio/types'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { selectors, actions, SectionNames, ResumeFieldSuggest } from 'builder/modules/resumeEditor'
import { useI18n } from 'builder/hooks/useI18n'
import FocusManager from 'builder/services/FocusManager'
import { generateRandomId } from 'builder/utils/generateRandomId'
import { getEmptyCard } from 'builder/modules/resumeEditor/resumeScoreRules/sectionRule'
import { useAiResumeReviewHighlightState } from 'builder/components/AiFeatures/AiResumeReview'
import { useRemainingAiResumeReviewFreeAttempts } from 'builder/hooks/useRemainingAiResumeReviewFreeAttempts'
import { useResumeReviewFeatureConfig } from 'builder/hooks/useResumeReviewFeatureConfig'
import { BACK_BUTTON_CONDITION_WITH_SCORE_THRESHOLD, LOCALIZATION_PREFIX } from '../constants'
import { constructSectionsData } from '../constructSectionsData'
import { useStepByStepFeature } from './useStepByStepFeature'

export const useStepByStepFlow = () => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const resume = useTypedSelector(selectors.resume)
  const currentStep = useTypedSelector(selectors.stepByStepFlowCurrentStep)
  const { shouldRedirectToAdditionalStep } = useTypedSelector(selectors.stepByStepFlow)
  const { currentStepEventName, isRbpStepFlowActive } = useStepByStepFeature()
  const { suggestions } = useTypedSelector(selectors.scoreSuggestions)
  const resumeOptimizerData = useTypedSelector(selectors.jobPostingAPIData)
  const resumeScore = resumeOptimizerData.averageScore || resume?.resumeScore || 0
  const { isAiResumeReviewFeatureActive } = useResumeReviewFeatureConfig()
  const { activeSections: sections, additionalSections } = resume
    ? constructSectionsData(resume, isAiResumeReviewFeatureActive)
    : { activeSections: [], additionalSections: [] }

  const { isAiResumeReviewSwitcherSeen, setIsAiResumeReviewSwitcherSeen } =
    useAiResumeReviewHighlightState()
  const remainingAttempts = useRemainingAiResumeReviewFreeAttempts()
  const isAiResumeReviewSectionActive =
    sections.find(section => section.id === 'aiResumeReview') && isAiResumeReviewFeatureActive
  const scoreImprovementSuggestion: ResumeFieldSuggest = useMemo(() => {
    const requiredScore = 100 - resumeScore

    const item = suggestions?.find(item => {
      if (currentStep === SectionNames.addAdditionalSection) {
        return additionalSections.some(
          section => section.id === item.sectionId || section.id === item.sectionName,
        )
      }
      return item.sectionId === currentStep || item.sectionName === currentStep
    })

    if (!item) return { text: '', points: 0, type: 'section', key: '' }

    return {
      fieldId: item.fieldId,
      type: item.type,
      text: item.text,
      points: Math.min(item.points, requiredScore),
      key: item.key,
      sectionName: item.sectionName,
    }
  }, [additionalSections, currentStep, resumeScore, suggestions])

  const currentIndex = useMemo(() => {
    if (!sections) return -1
    return sections.findIndex(
      item =>
        item.id === currentStep ||
        item.sortId === currentStep ||
        item.sortId === `custom:${currentStep}`,
    )
  }, [currentStep, sections])

  const handleChangeStep = useCallback(
    (sectionId: StepByStepFlowStepId) => {
      dispatch(actions.setStepByStepFlowState({ shouldRedirectToAdditionalStep: true }))
      dispatch(
        actions.updateSimpleField({
          name: 'data',
          value: {
            stepByStepFlow: {
              currentStep: sectionId,
            },
          },
          debounce: true,
        }),
      )
    },
    [dispatch],
  )

  const handleBack = useCallback(() => {
    if (!sections) return
    const previousStep = sections[currentIndex + -1]?.id
    dispatch(actions.setStepByStepFlowState({ shouldRedirectToAdditionalStep: false }))
    if (currentIndex > 0) {
      dispatch(
        actions.updateSimpleField({
          name: 'data',
          value: {
            stepByStepFlow: {
              currentStep: previousStep ?? currentStep,
            },
          },
          debounce: true,
        }),
      )
      if (currentStep) {
        trackInternalEvent('click_back', {
          label: 'step_builder',
          step: currentStepEventName,
          resume_id: resume?.id,
        })
      }
    } else {
      dispatch(
        actions.updateSimpleField({
          name: 'showPrefillResumeModal',
          value: true,
          debounce: true,
        }),
      )
    }
  }, [sections, currentIndex, dispatch, currentStep, currentStepEventName, resume?.id])

  const handleNext = useCallback(() => {
    if (!sections) return
    const nextStep = sections[currentIndex + 1]?.id
    if (currentStep === SectionNames.addAdditionalSection && isAiResumeReviewSectionActive) {
      dispatch(
        actions.updateSimpleField({
          name: 'data',
          value: {
            stepByStepFlow: {
              currentStep: 'aiResumeReview',
            },
          },
          debounce: true,
        }),
      )
      dispatch(actions.setStepByStepFlowState({ shouldRedirectToAdditionalStep: false }))
    } else if (shouldRedirectToAdditionalStep) {
      dispatch(
        actions.updateSimpleField({
          name: 'data',
          value: {
            stepByStepFlow: {
              currentStep: SectionNames.addAdditionalSection,
            },
          },
          debounce: true,
        }),
      )
      dispatch(actions.setStepByStepFlowState({ shouldRedirectToAdditionalStep: false }))
    } else if (nextStep) {
      dispatch(
        actions.updateSimpleField({
          name: 'data',
          value: {
            stepByStepFlow: {
              currentStep: nextStep,
            },
          },
          debounce: true,
        }),
      )
      if (currentStep) {
        trackInternalEvent('click_next_step', {
          label: 'step_builder',
          step: currentStepEventName,
          resume_id: resume?.id,
        })
      }
      dispatch(actions.setStepByStepFlowState({ shouldRedirectToAdditionalStep: false }))
    } else {
      dispatch(
        actions.updateSimpleField({
          name: 'data',
          value: {
            stepByStepFlow: {
              currentStep: null,
            },
          },
          debounce: true,
        }),
      )
      dispatch(actions.setStepByStepFlowState({ shouldRedirectToAdditionalStep: false }))
      dispatch(actions.setIsCustomizeModalOpen(true))
      trackInternalEvent('open_full_screen_resume_preview')
      if (!isAiResumeReviewFeatureActive) {
        trackInternalEvent('click_finish', {
          label: 'step_builder',
          resume_id: resume?.id,
          score: resumeScore,
        })
      }
    }
  }, [
    sections,
    currentIndex,
    currentStep,
    isAiResumeReviewSectionActive,
    shouldRedirectToAdditionalStep,
    dispatch,
    currentStepEventName,
    resume?.id,
    isAiResumeReviewFeatureActive,
    resumeScore,
  ])

  const currentSection = useMemo(() => {
    if (!sections) return null

    const section = sections[currentIndex]

    if (!section) {
      return sections.find(item => item.id === SectionNames.addAdditionalSection)
    } else {
      return section
    }
  }, [sections, currentIndex])

  const nextButtonText = useMemo(() => {
    if (!sections) return
    if (shouldRedirectToAdditionalStep) {
      return i18n.t(`${LOCALIZATION_PREFIX}.navigation_container.done_button_text`)
    } else if (
      currentStep === SectionNames.addAdditionalSection &&
      !isAiResumeReviewSectionActive
    ) {
      return i18n.t(`${LOCALIZATION_PREFIX}.navigation_container.finish_button_text`)
    } else {
      return `${i18n.t(`${LOCALIZATION_PREFIX}.navigation_container.next_button_text`)}: ${
        sections[currentIndex + 1]?.title
      }`
    }
  }, [
    sections,
    shouldRedirectToAdditionalStep,
    currentStep,
    isAiResumeReviewSectionActive,
    i18n,
    currentIndex,
  ])

  const isFinishStep = useMemo(() => {
    return currentIndex === sections.length - 1
  }, [currentIndex, sections.length])

  const isBackButtonPassive = useMemo(() => {
    return currentIndex === 0 && resumeScore >= BACK_BUTTON_CONDITION_WITH_SCORE_THRESHOLD
  }, [currentIndex, resumeScore])

  const handleSuggestionClick = useCallback(
    (suggestion: ResumeFieldSuggest) => {
      if (suggestion.type === 'simpleField' && suggestion.fieldId) {
        FocusManager.focus(suggestion.fieldId)
      }
      const sectionName = suggestion.sectionName
      if (suggestion.type === 'section' && sectionName) {
        const emptyCard = resume && getEmptyCard(sectionName, resume)

        if (emptyCard) {
          const cardId = emptyCard.cid || emptyCard.id
          dispatch(actions.openCard({ id: cardId, sectionId: sectionName }))
          FocusManager.focus(cardId)
        } else {
          dispatch(
            actions.addCard({
              sectionName,
              cardId: generateRandomId(),
              options: { scrollIntoViewport: false, shouldOpen: true },
            }),
          )
        }
        handleChangeStep(sectionName)
      }
    },
    [dispatch, handleChangeStep, resume],
  )

  const handleAIReview = useCallback(() => {
    if (!isAiResumeReviewSwitcherSeen) {
      setIsAiResumeReviewSwitcherSeen(true)
    }
    if (remainingAttempts > 0) {
      dispatch(actions.startAiResumeReview())
      dispatch(actions.setEditorPanelType('ai_review'))
      trackInternalEvent('click_get_better', {
        label: 'step_flow_ai_review',
      })
    } else {
      dispatch(actions.setIsAiResumeReviewPaywallModalOpen(true))
    }

    dispatch(
      actions.updateSimpleField({
        name: 'data',
        value: {
          stepByStepFlow: {
            currentStep: null,
          },
        },
        debounce: true,
      }),
    )
    dispatch(actions.setStepByStepFlowState({ shouldRedirectToAdditionalStep: false }))
  }, [dispatch, isAiResumeReviewSwitcherSeen, remainingAttempts, setIsAiResumeReviewSwitcherSeen])

  const handleSkipAIReview = useCallback(() => {
    trackInternalEvent('click_skip', {
      label: 'step_flow_ai_review',
      resume_id: resume?.id,
      score: resumeScore,
    })
    if (!isAiResumeReviewSwitcherSeen) {
      setIsAiResumeReviewSwitcherSeen(true)
    }
    handleNext()
  }, [
    handleNext,
    isAiResumeReviewSwitcherSeen,
    resume?.id,
    resumeScore,
    setIsAiResumeReviewSwitcherSeen,
  ])

  return {
    nextButtonText,
    currentSection,
    handleBack,
    handleNext,
    handleChangeStep,
    sections,
    currentIndex,
    isBackButtonPassive,
    scoreImprovementSuggestion,
    handleSuggestionClick,
    resumeScore,
    handleAIReview,
    handleSkipAIReview,
    isFinishStep,
    isRbpStepFlowActive,
  }
}
